import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {ColumnDef} from "@tanstack/react-table";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import {DigitalSigningStatusElement} from "../../common/uiElements/DigitalSigningStatusElement";
import SettingsListPage from "../../system/layout/SettingsListPage";
import DigitalSigningDetailsDialog from "../details/DigitalSigningDetailsDialog";
import {serviceSummary} from "../details/digitalSigningDetails.util";
import {DigitalSigningListRow, DigitalSigningListSortField} from "../digitalSigning.model";
import {DigitalSigningService} from "../digitalSigning.service";
import {formatMoney} from "../public/format.util";
import {digitalSigningFilterOptions, listRequest} from "./digitalSigningList.util";

export const DIGITAL_SIGNINGS_PATH = '/system/signatures';
export const DIGITAL_SIGNINGS_TABLE_ID = 'system-digital-signings-v5';

const dateTime = (value?: Date | string) => formatFullDateTime(value ? new Date(value) : undefined);

const sortedBy = (field: DigitalSigningListSortField) => ({isServerSortable: true, serverSortField: field});

/** When it happened, and below it in grey the staff member who did it. */
const DateByCell = ({date, by}: {date?: Date | string, by?: string}) => {
    const {t} = useTranslation();
    return (
        <Stack gap={0.25} minWidth={0}>
            <Box>{dateTime(date)}</Box>
            {!!date && !!by && (
                <Typography variant={'body2'} color={'text.secondary'} noWrap>{t('by {{name}}', {name: by})}</Typography>
            )}
        </Stack>
    );
};

const DigitalSigningsPage = () => {
    useTrackScreenVisit('system signatures');

    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {id} = useParams();
    const language = i18n.language || 'en';
    const filterOptions = useMemo(() => digitalSigningFilterOptions(t), [t]);

    const columns = useMemo<ColumnDef<DigitalSigningListRow>[]>(() => {
        const money = (cents: number | undefined, currency?: string) => (cents == null ? '' : formatMoney(cents, currency, language, false, 'code'));

        return [
            {
                id: 'issued',
                header: t('Issued'),
                accessorFn: row => row.issuedAt,
                cell: ({row}) => <Box>{dateTime(row.original.issuedAt)}</Box>,
                size: 150,
                meta: {defaultActive: true, ...sortedBy('issuedAt')},
            },
            {
                id: 'service',
                header: t('Service'),
                accessorFn: row => row.serviceName,
                cell: ({row}) => <Box>{serviceSummary(row.original, language)}</Box>,
                size: 170,
                meta: {defaultActive: true, grow: true, ...sortedBy('serviceName')},
            },
            {
                id: 'guest',
                header: t('Guest'),
                accessorFn: row => row.guestName,
                cell: ({row}) => <Box>{row.original.guestName?.trim() || t('Unknown')}</Box>,
                size: 150,
                meta: {defaultActive: true, ...sortedBy('guestName')},
            },
            {
                id: 'status',
                header: t('Status'),
                accessorFn: row => row.status,
                cell: ({row}) => <DigitalSigningStatusElement status={row.original.status}/>,
                size: 140,
                meta: {defaultActive: true, ...sortedBy('status')},
            },
            {
                id: 'signed',
                header: t('Signed'),
                accessorFn: row => row.signedAt,
                cell: ({row}) => <Box>{dateTime(row.original.signedAt)}</Box>,
                size: 150,
                meta: {defaultActive: true, ...sortedBy('signedAt')},
            },
            {
                id: 'tip',
                header: t('Tip'),
                accessorFn: row => row.tip?.amountCents,
                cell: ({row: {original}}) => {
                    const cents = original.tip?.amountCents ?? 0;
                    return (
                        <Box sx={{fontVariantNumeric: 'tabular-nums'}}>
                            {cents > 0 ? money(cents, original.currency ?? original.tip?.currency) : original.signedAt ? '–' : ''}
                        </Box>
                    );
                },
                size: 100,
                meta: {defaultActive: true, align: 'right', ...sortedBy('tip')},
            },
            {
                id: 'confirmed',
                header: t('Confirmed'),
                accessorFn: row => row.confirmedAt,
                cell: ({row}) => <DateByCell date={row.original.confirmedAt} by={row.original.confirmedByUserName}/>,
                size: 160,
                meta: {defaultActive: true, ...sortedBy('confirmedAt')},
            },
            {
                id: 'template',
                header: t('Template'),
                accessorFn: row => row.templateName,
                cell: ({row}) => <Box>{row.original.templateName ?? ''}</Box>,
                size: 180,
                meta: {defaultActive: false},
            },
            {
                id: 'id',
                header: t('ID'),
                accessorFn: row => row.id,
                cell: ({row}) => <Box>{row.original.id}</Box>,
                size: 260,
                meta: {defaultActive: false, ...sortedBy('id')},
            },
        ];
    }, [t, language]);

    const controller = useServerTable<DigitalSigningListRow>({
        tableId: DIGITAL_SIGNINGS_TABLE_ID,
        columns,
        getRowId: row => row.id,
        initialSorting: [{id: 'issued', desc: true}],
        onRowClick: row => navigate({pathname: `${DIGITAL_SIGNINGS_PATH}/${row.id}`, search: location.search}),
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const page = await DigitalSigningService.listRows(listRequest(filterRules, searchQuery, pagination, sorting));
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        },
    });

    const closeDetails = () => navigate({pathname: DIGITAL_SIGNINGS_PATH, search: location.search});

    return (
        <SettingsListPage title={t('Signatures')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch placeholder={t('Guest or staff')}/>
                        <ServerTableSort/>
                        <ServerTableFilter options={filterOptions}/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            <DigitalSigningDetailsDialog id={id} open={!!id} onClose={closeDetails}/>
        </SettingsListPage>
    );
};

export default DigitalSigningsPage;
